/**
 * LukaLoadingScreen.tsx
 * src/shared/components/LukaLoadingScreen.tsx
 *
 * Branded loading screen shown while the app warms up (profile load).
 * Replaces the bare "warm sand" flash that used to show in the gap
 * between the native splash and the Timeline.
 *
 * Visual: Luka's spiral-and-face logo (vector-traced from the app icon)
 * in white on the brand green, matching the app icon and native splash
 * for seamless continuity, with the message "Luka is getting things
 * ready for you".
 *
 * Three UX principles baked in:
 *   - Minimum display time (MIN_DISPLAY_MS): even if data loads
 *     instantly, the screen shows long enough to read as intentional
 *     rather than a flicker.
 *   - Smooth fade out: when ready, the screen fades rather than
 *     hard-cutting to the app.
 *   - Pixel-identical to #boot-splash in index.html, so the handoff
 *     from the static splash to this component is invisible. If you
 *     change the green, the logo size, the message, or the message
 *     styling here, change it there too. See the SPLASH CONTINUITY
 *     note on the message below.
 *
 * September 2026:
 *   - Message font changed from Lora to the system serif, and the
 *     message given a fixed height. Lora now ships with the app bundle
 *     rather than loading from Google, so it is not available when
 *     #boot-splash paints. The message was therefore rendering in a
 *     fallback face there and in Lora here, and because the column is
 *     centred, a one-line to two-line difference moved the logo between
 *     the two screens. Fixed height plus no web font means the layout
 *     cannot shift.
 *   - Removed a dead content fade-in. contentShown was initialised to
 *     true, so the effect set true to true and the transition never
 *     ran. It was left over from an earlier fix for this same flicker.
 */

import { useState, useEffect } from 'react'

// Brand green - intentionally hardcoded to match the app icon and the
// theme-color meta tag in index.html. Fixed brand identity colour, NOT
// theme-dependent (same rationale as PILLAR_COLOURS in AppShell). If a
// shared --ui/--brand token for this green exists, swap it in here.
const BRAND_GREEN = '#5B7A5E'
const MIN_DISPLAY_MS = 900   // minimum time the screen stays visible
const FADE_MS = 350          // fade-out duration

interface LukaLoadingScreenProps {
  ready: boolean   // true once the app has finished loading
}

export function LukaLoadingScreen({ ready }: LukaLoadingScreenProps) {
  const [minTimeElapsed, setMinTimeElapsed] = useState(false)
  const [unmounted,      setUnmounted]      = useState(false)

  // Enforce the minimum display time
  useEffect(() => {
    const t = window.setTimeout(() => setMinTimeElapsed(true), MIN_DISPLAY_MS)
    return () => window.clearTimeout(t)
  }, [])

  // Once ready AND the minimum time has passed, fade out then unmount
  const shouldFade = ready && minTimeElapsed
  useEffect(() => {
    if (!shouldFade) return
    const t = window.setTimeout(() => setUnmounted(true), FADE_MS)
    return () => window.clearTimeout(t)
  }, [shouldFade])

  if (unmounted) return null

  return (
    <div
      style={{
        position:       'fixed' as const,
        inset:           0,
        zIndex:          9999,
        background:      BRAND_GREEN,
        display:        'flex',
        flexDirection:  'column' as const,
        alignItems:     'center',
        justifyContent: 'center',
        opacity:         shouldFade ? 0 : 1,
        transition:     `opacity ${FADE_MS}ms ease`,
        pointerEvents:   shouldFade ? 'none' : 'auto',
      }}
    >
      <div
        style={{
          display:       'flex',
          flexDirection: 'column' as const,
          alignItems:    'center',
          gap:            24,
        }}
      >
        {/* Luka's spiral-and-face logo - vector-traced from icon-512.png */}
        <svg width="240" height="240" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <g transform="translate(0,512) scale(0.1,-0.1)" fill="white" stroke="none">
            <path d="M2436 4245 c-644 -92 -1237 -474 -1553 -1000 -484 -806 -260 -1905 492 -2413 166 -112 274 -152 308 -114 43 48 23 89 -66 135 -388 198 -677 565 -792 1004 -81 313 -71 695 26 988 224 670 922 1196 1682 1266 500 46 1007 -213 1284 -656 334 -534 271 -1294 -142 -1711 -376 -378 -1047 -510 -1499 -294 -619 295 -911 1158 -580 1712 238 398 800 653 1209 548 324 -83 497 -307 497 -645 1 -243 -69 -445 -232 -675 -105 -148 -121 -165 -205 -206 -223 -109 -529 -37 -658 154 -45 66 -52 72 -89 72 -32 0 -68 -34 -68 -64 0 -68 125 -206 242 -266 307 -160 673 -93 841 154 134 198 169 255 205 338 152 346 142 718 -26 965 -165 244 -517 380 -852 330 -531 -80 -982 -456 -1107 -922 -40 -152 -40 -455 1 -629 171 -730 766 -1182 1456 -1106 557 60 985 334 1205 771 173 341 204 825 80 1205 -233 711 -940 1162 -1659 1059z" />
            <path d="M2155 3070 c-91 -28 -161 -140 -152 -242 11 -123 89 -200 208 -206 288 -17 313 432 25 454 -28 2 -65 -1 -81 -6z m215 -70 c43 -43 11 -120 -49 -120 -33 0 -71 39 -71 72 0 53 82 86 120 48z" />
            <path d="M2875 3011 c-161 -74 -169 -322 -13 -402 96 -49 212 -8 262 92 91 183 -75 390 -249 310z m217 -58 c37 -33 11 -103 -39 -103 -12 0 -32 9 -43 20 -54 54 25 134 82 83z" />
          </g>
        </svg>

        {/*
          Warm reassurance message.

          SPLASH CONTINUITY - must stay identical to the
          "#boot-splash .boot-line" rule in index.html.

          Georgia, not Lora, on purpose. Lora ships with the app bundle,
          so it is not loaded when #boot-splash paints. Using it here and
          a fallback face there made the text reflow between the two
          screens, and a centred column means a taller text block pushes
          the logo up. The system serif is available to both.

          The fixed 48px height is two lines at 16px and 1.5 line-height,
          so the block is the same size whether the message wraps to one
          line or two. Without it, a different phone width could start
          the logo moving again.
        */}
        <div
          style={{
            fontFamily:     'Georgia, serif',
            fontSize:        16,
            fontStyle:      'italic',
            color:          'rgba(255,255,255,0.95)',
            letterSpacing:  '0.01em',
            textAlign:      'center' as const,
            padding:        '0 32px',
            lineHeight:      1.5,
            height:          48,
            display:        'flex',
            alignItems:     'flex-start',
            justifyContent: 'center',
          }}
        >
          Luka is getting things ready for you
        </div>
      </div>
    </div>
  )
}
